import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const SiteConfigContext = createContext();

export const useSiteConfig = () => {
  const context = useContext(SiteConfigContext);
  if (!context) {
    throw new Error('useSiteConfig must be used within SiteConfigProvider');
  }
  return context;
};

// Default configuration
const defaultConfig = {
  pages: {
    home: true,
    about: true,
    services: true,
    culture: true,
    jobs: true,
    apply: true,
    refer: true,
    contact: true,
    track: true,
  },
  sections: {
    home: {
      hero: true,
      stats: true,
      about: true,
      process: true,
      jobs: true,
      team: true,
      testimonials: true,
      cta: true
    },
    services: {
      rpo: true,
      staffing: true,
      hr_consulting: false,
      executive_search: false
    },
    navigation: {
      hr_consulting: false,
      executive_search: false
    }
  },
  branding: {
    company_name: "Tech5Sages Global",
    tagline: "Transforming Careers, Building Futures",
    logo_url: "https://customer-assets.emergentagent.com/job_tech5recruit/artifacts/r8hillf7_PHOTO-2024-04-10-21-03-06%203%202.jpg",
    colors: {
      primary: "#1E3A5F",
      secondary: "#0891B2",
      accent: "#D4A853"
    }
  },
  hero_content: {
    home: {
      title: "Transforming Careers,",
      title_highlight: "Building Futures",
      subtitle: "Connecting exceptional talent with leading organizations worldwide.",
      cta_primary: "Get Started",
      cta_primary_link: "/apply",
      cta_secondary: "Contact Us",
      cta_secondary_link: "/contact"
    },
    about: {
      title: "About",
      title_highlight: "Tech5Sages Global",
      subtitle: "A Diverse Network of Experts and Industry Professionals"
    },
    services: {
      title: "Comprehensive Recruitment",
      title_highlight: "Solutions",
      subtitle: "End-to-end hiring solutions tailored to your business needs"
    },
    jobs: {
      title: "Find Your",
      title_highlight: "Dream Job",
      subtitle: "Explore opportunities from top companies"
    },
    contact: {
      title: "Let's",
      title_highlight: "Connect",
      subtitle: "We'd love to hear from you"
    },
    culture: {
      title: "Our Culture",
      title_highlight: "& Values",
      subtitle: "What drives us forward"
    }
  },
  stats: {
    active_jobs: { value: 100, suffix: "+", label: "Active Jobs" },
    partner_companies: { value: 50, suffix: "+", label: "Partner Companies" },
    successful_placements: { value: 500, suffix: "+", label: "Successful Placements" },
    success_rate: { value: 98, suffix: "%", label: "Success Rate" }
  },
  services: [],
  team: [],
  testimonials: [],
  contact: {
    address: "123 Business Park, Tech City, India",
    phone: "+91 1234567890",
    email: "info@tech5sages.com",
    hours: "Mon-Fri: 9AM - 6PM IST"
  },
  social: {
    linkedin: "",
    twitter: "",
    facebook: "",
    instagram: "",
    youtube: ""
  },
  footer: {
    copyright: "© 2024 Tech5Sages Global. All rights reserved.",
    description: "Your trusted partner in talent acquisition."
  },
  seo: {
    site_title: "Tech5Sages Global - Recruitment & Talent Acquisition",
    site_description: "Premium recruitment and talent acquisition services."
  },
  navigation: {
    items: [],
    cta_buttons: []
  },
  features: {
    job_sharing: true,
    whatsapp_share: true,
    email_share: true,
    application_tracking: true,
    referral_system: true,
    hot_jobs_section: true,
    testimonials_section: true,
    team_section: true
  },
  nav_order: ["home", "services", "careers", "culture", "contact", "about"]
};

export const SiteConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(defaultConfig);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API}/site-config`);
        if (res.data) {
          // Deep merge with defaults to ensure all fields exist
          setConfig(prev => deepMerge(prev, res.data));
        }
      } catch (error) {
        console.log('Using default site config');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // Deep merge utility
  const deepMerge = (target, source) => {
    const output = { ...target };
    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        output[key] = deepMerge(target[key] || {}, source[key]);
      } else {
        output[key] = source[key];
      }
    }
    return output;
  };

  const isPageVisible = (pageName) => {
    return config.pages?.[pageName] !== false;
  };

  const isSectionVisible = (pageName, sectionName) => {
    return config.sections?.[pageName]?.[sectionName] !== false;
  };

  const getContent = (pageName, key, defaultValue = '') => {
    return config.content?.[pageName]?.[key] || defaultValue;
  };

  const getHeroContent = (pageName) => {
    return config.hero_content?.[pageName] || defaultConfig.hero_content[pageName] || {};
  };

  const getBranding = () => {
    return config.branding || defaultConfig.branding;
  };

  const getStats = () => {
    return config.stats || defaultConfig.stats;
  };

  const getServices = () => {
    return (config.services || []).filter(s => s.enabled !== false);
  };

  const getTeam = () => {
    return (config.team || []).filter(t => t.enabled !== false);
  };

  const getTestimonials = () => {
    return (config.testimonials || []).filter(t => t.enabled !== false);
  };

  const getContact = () => {
    return config.contact || defaultConfig.contact;
  };

  const getSocial = () => {
    return config.social || defaultConfig.social;
  };

  const getFooter = () => {
    return config.footer || defaultConfig.footer;
  };

  const getSeo = () => {
    return config.seo || defaultConfig.seo;
  };

  const getNavigation = () => {
    return config.navigation || defaultConfig.navigation;
  };

  const isFeatureEnabled = (featureName) => {
    return config.features?.[featureName] !== false;
  };

  const getNavOrder = () => {
    return config.nav_order || defaultConfig.nav_order;
  };

  const refreshConfig = async () => {
    try {
      const res = await axios.get(`${API}/site-config`);
      if (res.data) {
        setConfig(prev => deepMerge(prev, res.data));
      }
    } catch (error) {
      console.error('Error refreshing config:', error);
    }
  };

  return (
    <SiteConfigContext.Provider value={{
      config,
      loading,
      isPageVisible,
      isSectionVisible,
      getContent,
      getHeroContent,
      getBranding,
      getStats,
      getServices,
      getTeam,
      getTestimonials,
      getContact,
      getSocial,
      getFooter,
      getSeo,
      getNavigation,
      isFeatureEnabled,
      getNavOrder,
      refreshConfig
    }}>
      {children}
    </SiteConfigContext.Provider>
  );
};

export default SiteConfigContext;
